/**
 * Dictation — reusable voice-to-text for any text box in the dashboard.
 *
 *   const dictation = useDictation({ onText: t => setValue(v => (v ? `${v} ${t}` : t)) });
 *   <MicButton dictation={dictation} />
 *
 * Records with MediaRecorder and transcribes on the server (POST /api/process_audio ->
 * Whisper, billed as 'whisper' to the expert) — the same pipeline the chat input uses
 * (ConversationView/components/MessageInput.jsx), extracted so new features don't copy it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const t of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4;codecs=mp4a.40.2', 'audio/webm']) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return 'audio/webm';
};
const extFor = (mime) => (mime.includes('ogg') ? 'ogg' : mime.includes('mp4') ? 'mp4' : 'webm');

export function useDictation({ onText }) {
  const axios = useAuthenticatedAxios();
  const { user } = useAuth0();
  const { showToast } = useToast();
  const [state, setState] = useState('idle');          // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const transcribe = useCallback(async (chunks, mime) => {
    setState('transcribing');
    try {
      const form = new FormData();
      form.append('file', new Blob(chunks, { type: mime }), `dictation_${Date.now()}.${extFor(mime)}`);
      const r = await axios.post('/api/process_audio', form, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Expert-ID': user?.sub || '' },
        timeout: 60000,
      });
      const text = (r.data?.transcription || '').trim();
      if (!text) showToast("Couldn't hear anything — please try again and speak clearly.", 'warning');
      else onTextRef.current(text);
    } catch (e) {
      const status = e?.response?.status;
      showToast(status === 413 ? 'Recording too long — please record in shorter parts.'
        : e?.code === 'ECONNABORTED' ? 'Transcription timed out — please try a shorter recording.'
        : 'Could not transcribe the recording. Please try again.', 'error');
    } finally { setState('idle'); }
  }, [axios, user, showToast]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
      const mime = pickMimeType();
      const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128000 });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        transcribe(chunks, mime);
      };
      rec.start(1000);
      recorderRef.current = rec;
      setSeconds(0);
      setState('recording');
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch {
      showToast('Could not access your microphone. Please check permissions and try again.', 'error');
    }
  }, [transcribe, showToast]);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    recorderRef.current = null;
  }, []);

  const toggle = useCallback(() => (state === 'recording' ? stop() : state === 'idle' ? start() : undefined), [state, start, stop]);

  // Release the microphone if the component unmounts mid-recording.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') { rec.onstop = () => rec.stream?.getTracks().forEach(t => t.stop()); rec.stop(); }
  }, []);

  return { state, seconds, toggle, supported: typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices };
}

const pulse = keyframes`0%{box-shadow:0 0 0 0 rgba(239,68,68,.5)}70%{box-shadow:0 0 0 10px rgba(239,68,68,0)}100%{box-shadow:0 0 0 0 rgba(239,68,68,0)}`;
const Mic = styled.button`
  display: inline-flex; align-items: center; gap: 8px; padding: 9px 14px; border-radius: 10px; cursor: pointer;
  font-size: 13.5px; font-weight: 600; color: #fff; white-space: nowrap;
  border: 1px solid ${p => (p.$rec ? 'rgba(239,68,68,0.7)' : 'rgba(120,140,180,0.25)')};
  background: ${p => (p.$rec ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.06)')};
  animation: ${p => (p.$rec ? pulse : 'none')} 1.4s infinite;
  &:disabled { opacity: 0.6; cursor: wait; }
  svg { width: 16px; height: 16px; }
`;

export function MicButton({ dictation, idleLabel = 'Dictate' }) {
  if (!dictation.supported) return null;
  const { state, seconds, toggle } = dictation;
  const mm = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return (
    <Mic type="button" $rec={state === 'recording'} disabled={state === 'transcribing'} onClick={toggle}
      title={state === 'recording' ? 'Stop and transcribe' : 'Speak instead of typing'}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0014 0M12 17v5M8 22h8" />
      </svg>
      {state === 'recording' ? `Stop · ${mm}` : state === 'transcribing' ? 'Transcribing…' : idleLabel}
    </Mic>
  );
}
